import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import AddEmployeeModal from '../components/AddEmployeeModal';
import StatStrip from '../components/team/StatStrip';
import FilterBar from '../components/team/FilterBar';
import EmployeeTable from '../components/team/EmployeeTable';
import WhatsappInviteModal from '../components/team/WhatsappInviteModal';
import DeactivateConfirmModal from '../components/team/DeactivateConfirmModal';
import LeaveTab from '../components/team/LeaveTab';
import { useSubscription } from '../contexts/SubscriptionContext';
import apiClient from '../api/axios';

const WHATSAPP_POLL_INTERVAL_MS = 4000;

function applyFilters(employees, { search, role, status }) {
  return employees.filter(e => {
    if (status === 'active' && !e.isActive) return false;
    if (status === 'inactive' && e.isActive) return false;
    if (role && e.jobRole !== role) return false;
    if (search) {
      const q = search.toLowerCase();
      const nameMatch = e.fullName?.toLowerCase().includes(q);
      const phoneMatch = e.phone?.includes(q);
      if (!nameMatch && !phoneMatch) return false;
    }
    return true;
  });
}

function SkeletonTable() {
  return (
    <div className="mx-6 mb-6 rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
      <div className="bg-gray-50 border-b border-gray-200 h-10" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-gray-100 last:border-0">
          <div className="w-7 h-7 rounded-full bg-gray-200 animate-pulse shrink-0" />
          <div className="flex-1 h-3 rounded bg-gray-200 animate-pulse max-w-[140px]" />
          <div className="w-20 h-3 rounded bg-gray-200 animate-pulse" />
          <div className="flex-1 h-3 rounded bg-gray-100 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function Team() {
  const { t } = useTranslation('team');
  const { t: tCommon } = useTranslation('common');
  const { isGated, status: subscriptionStatus, refetch: refetchSubscriptionStatus } = useSubscription();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('members');
  const [employees, setEmployees] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [toast, setToast] = useState(null);

  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('active');

  const [inviteEmployee, setInviteEmployee] = useState(null);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteConsentChecked, setInviteConsentChecked] = useState(false);
  const [inviteAgeChecked, setInviteAgeChecked] = useState(false);
  const employeesRef = useRef(employees);

  // Story 22 — deactivation-shift-cleanup confirm dialog + its default (from Settings).
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateInFlight, setDeactivateInFlight] = useState(false);
  const [defaultRemoveUpcoming, setDefaultRemoveUpcoming] = useState(true);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/employees?size=100&includeInactive=true');
      setEmployees(res.data?.content ?? []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load employees. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Story 30 — fetch restaurant once on mount, for managerWhatsappStatus (mirrors Scheduler.jsx).
  useEffect(() => {
    apiClient
      .get('/api/restaurants/me')
      .then((r) => setRestaurant(r.data))
      .catch(() => {});
  }, []);

  // Fetches the restaurant's deactivation-shift-cleanup default. OWNER-only endpoint —
  // a MANAGER viewing this page simply keeps the built-in true default (silent catch).
  useEffect(() => {
    apiClient.get('/api/settings/deactivation-shifts')
      .then(res => setDefaultRemoveUpcoming(!!res.data?.removeUpcomingShiftsOnDeactivation))
      .catch(() => {});
  }, []);

  const handleEmployeeAdded = (name) => {
    setToast(`${name} added successfully.`);
    fetchEmployees();
    // Story 32: keep the staff-cap badge/gate in sync immediately, rather than waiting
    // for SubscriptionContext's 5-minute poll.
    refetchSubscriptionStatus();
  };

  const handleEmployeeUpdated = (updated) => {
    setEmployees(prev => prev.map(e => e.id === updated.id ? updated : e));
    setToast(`${updated.fullName} updated.`);
  };

  const performToggle = useCallback(async (emp, newActive, removeUpcomingShifts) => {
    setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, isActive: newActive } : e));
    try {
      const body = { active: newActive };
      if (removeUpcomingShifts !== undefined) body.removeUpcomingShifts = removeUpcomingShifts;
      await apiClient.patch(`/api/employees/${emp.id}`, body);
      setToast(`${emp.fullName} ${newActive ? 'reactivated' : 'deactivated'}.`);
      // Story 32: an activate/deactivate changes the staff-cap count immediately.
      refetchSubscriptionStatus();
    } catch (err) {
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, isActive: !newActive } : e));
      setToast(err.response?.data?.message || `Failed to update ${emp.fullName}.`);
    }
  }, [refetchSubscriptionStatus]);

  // Story 22 — reactivation goes straight through as before. Deactivation first checks
  // for removable upcoming shifts; if there are any, a confirm dialog is shown instead
  // of patching immediately (owner/manager decides whether to also remove them).
  const handleToggleActive = useCallback(async (emp) => {
    if (!emp.isActive) {
      await performToggle(emp, true);
      return;
    }
    try {
      const res = await apiClient.get(`/api/employees/${emp.id}/deactivation-impact`);
      const { upcomingShiftCount, lastShiftDate } = res.data ?? {};
      if (upcomingShiftCount > 0) {
        setDeactivateTarget({ employee: emp, shiftCount: upcomingShiftCount, lastShiftDate });
        return;
      }
    } catch {
      // Impact check failed — fall through to a plain deactivate rather than blocking the action.
    }
    await performToggle(emp, false);
  }, [performToggle]);

  const handleConfirmDeactivate = useCallback(async (removeShifts) => {
    if (!deactivateTarget) return;
    setDeactivateInFlight(true);
    await performToggle(deactivateTarget.employee, false, removeShifts);
    setDeactivateInFlight(false);
    setDeactivateTarget(null);
  }, [deactivateTarget, performToggle]);

  const closeDeactivateModal = useCallback(() => {
    if (deactivateInFlight) return;
    setDeactivateTarget(null);
  }, [deactivateInFlight]);

  const handleEditEmployee = useCallback((emp) => setEditEmployee(emp), []);

  useEffect(() => {
    employeesRef.current = employees;
  }, [employees]);

  const anyPending = employees.some(e => e.whatsappOptinStatus === 'PENDING');

  // Poll while any row is PENDING, so a webhook-driven status flip (opted in /
  // declined) shows up without a manual refresh. Mirrors settings/Notifications.jsx.
  // Gated on the derived anyPending boolean (not on `employees` itself) so the tick's
  // own setEmployees(updated) doesn't re-trigger this effect and re-arm the interval
  // every 4s — it now runs continuously for as long as anyPending stays true.
  useEffect(() => {
    if (!anyPending) return undefined;

    const timer = setInterval(async () => {
      try {
        const res = await apiClient.get('/api/employees?size=100&includeInactive=true');
        const updated = res.data?.content ?? [];
        const prev = employeesRef.current;
        const optedIn = updated.filter(u => {
          const before = prev.find(p => p.id === u.id);
          return before?.whatsappOptinStatus === 'PENDING' && u.whatsappOptinStatus === 'OPTED_IN';
        });
        if (optedIn.length > 0) {
          setToast(`${optedIn[0].fullName} opted in to WhatsApp.`);
        }
        setEmployees(updated);
      } catch {
        // silent — next poll tick will retry
      }
    }, WHATSAPP_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [anyPending]);

  const openInviteModal = useCallback((emp) => {
    setInviteError('');
    setInviteConsentChecked(false);
    setInviteAgeChecked(false);
    setInviteEmployee(emp);
  }, []);

  const closeInviteModal = () => {
    if (inviteSubmitting) return;
    setInviteEmployee(null);
    setInviteError('');
    setInviteConsentChecked(false);
    setInviteAgeChecked(false);
  };

  // Story 30 — owner must have verified their own WhatsApp before inviting staff.
  const ownerWhatsappVerified = restaurant?.managerWhatsappStatus === 'VERIFIED';
  const showWhatsappVerifyBanner = !!restaurant && !ownerWhatsappVerified;
  const inviteGated = isGated || !ownerWhatsappVerified;
  const inviteGatedMessage = isGated ? tCommon('gatedAction.tooltip') : t('whatsapp.ownerNotVerifiedHint');

  const handleConfirmInvite = async () => {
    if (!inviteEmployee || !inviteConsentChecked || !inviteAgeChecked || inviteGated) return;
    setInviteSubmitting(true);
    setInviteError('');
    try {
      const res = await apiClient.post(`/api/employees/${inviteEmployee.id}/whatsapp-invite`, {
        consent: true,
        ageConfirmed: true,
      });
      setEmployees(prev => prev.map(e => e.id === res.data.id ? res.data : e));
      setToast(`WhatsApp invite sent to ${res.data.fullName}.`);
      setInviteEmployee(null);
    } catch (err) {
      setInviteError(err.response?.data?.message || "Couldn't send the invite. Please try again.");
    } finally {
      setInviteSubmitting(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setRole('');
    setStatus('active');
  };

  const filtered = useMemo(
    () => applyFilters(employees, { search, role, status }),
    [employees, search, role, status]
  );
  const hasEmployees = employees.length > 0;

  // Story 32 — staff-cap counter + upsell. The cap itself only comes from
  // /api/subscription/status (Team.jsx has no other source for it), but the USED count
  // uses this page's own freshly-fetched employees list rather than that endpoint's
  // 5-minute-polled staffUsed — otherwise adding/reactivating someone would leave the
  // badge showing the pre-add count until the next poll.
  const staffCap = subscriptionStatus?.planLimits?.staffCap ?? null;
  const staffUsed = employees.filter(e => e.isActive).length;
  const atStaffCap = staffCap != null && staffUsed >= staffCap;
  const overStaffCap = staffCap != null && staffUsed > staffCap;
  const staffCapTooltip = subscriptionStatus?.subscribedPlan === 'STARTER'
    ? t('staffCap.reachedTooltipStarter', { cap: staffCap })
    : t('staffCap.reachedTooltipDefault', { cap: staffCap });
  const addBlocked = isGated || atStaffCap;
  const addBlockedTooltip = isGated ? tCommon('gatedAction.tooltip') : (atStaffCap ? staffCapTooltip : undefined);

  const staffCapBadge = (
    <span
      key="staffCapBadge"
      className={`text-sm font-medium whitespace-nowrap ${overStaffCap ? 'text-red-600' : 'text-gray-500'}`}
    >
      {staffCap == null
        ? t('staffCap.counterUnlimited', { used: staffUsed })
        : overStaffCap
        ? t('staffCap.counterOverCap', { used: staffUsed, cap: staffCap })
        : t('staffCap.counter', { used: staffUsed, cap: staffCap })}
    </span>
  );

  const addButton = (
    <button
      key="add"
      onClick={() => setIsAddOpen(true)}
      disabled={addBlocked}
      title={addBlockedTooltip}
      className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Plus size={16} />
      {t('addEmployee')}
    </button>
  );

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {toast && (
        <div className="fixed top-4 right-4 z-[60] flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 shadow-lg max-w-xs">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span className="flex-1">{toast}</span>
          <button onClick={() => setToast(null)} className="text-current opacity-60 hover:opacity-100 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={activeTab === 'members' ? [staffCapBadge, addButton] : []}
      />

      <div className="px-6 pt-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
              activeTab === 'members' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('tabs.members')}
          </button>
          <button
            onClick={() => setActiveTab('leave')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition ${
              activeTab === 'leave' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t('tabs.leave')}
          </button>
        </div>
      </div>

      {activeTab === 'leave' && (
        <div className="mt-4">
          <LeaveTab employees={employees} onToast={setToast} />
        </div>
      )}

      {activeTab === 'members' && (
      <>
      {/* Story 30 — proactive prompt so the owner isn't surprised at invite time */}
      {showWhatsappVerifyBanner && (
        <div className="mx-6 mt-4 mb-4 flex items-center justify-between gap-4 flex-wrap rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p>{t('whatsapp.verifyBanner.message')}</p>
          <button
            type="button"
            onClick={() => navigate('/settings/notifications')}
            className="inline-flex items-center rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2"
          >
            {t('whatsapp.verifyBanner.cta')}
          </button>
        </div>
      )}

      {/* Error banner — shown when fetch fails but does NOT clear existing data */}
      {error && (
        <div className="mx-6 mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button
            onClick={fetchEmployees}
            className="inline-flex items-center gap-1.5 font-medium underline hover:no-underline"
          >
            <RefreshCw size={13} />
            Retry
          </button>
        </div>
      )}

      {/* Empty state — no employees at all */}
      {!loading && !error && !hasEmployees && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 text-center">
          <Users size={48} className="text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-700 mb-2">{t('empty.title')}</h2>
          <p className="text-sm text-gray-500 mb-6 max-w-sm">{t('empty.description')}</p>
          <button
            onClick={() => setIsAddOpen(true)}
            disabled={addBlocked}
            title={addBlockedTooltip}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={16} />
            {t('empty.cta')}
          </button>
        </div>
      )}

      {/* Populated state */}
      {(hasEmployees || loading) && (
        <>
          {!loading && (
            <StatStrip employees={employees} />
          )}

          {!loading && (
            <FilterBar
              search={search}
              onSearchChange={setSearch}
              role={role}
              onRoleChange={setRole}
              status={status}
              onStatusChange={setStatus}
            />
          )}

          {loading && <SkeletonTable />}

          {!loading && filtered.length > 0 && (
            <EmployeeTable
              employees={filtered}
              onEdit={handleEditEmployee}
              onToggleActive={handleToggleActive}
              onInvite={openInviteModal}
            />
          )}

          {!loading && filtered.length === 0 && hasEmployees && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users size={36} className="text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-600 mb-1">No employees match your filter</p>
              <p className="text-xs text-gray-400 mb-5">Try adjusting the search or role filter</p>
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50 transition"
              >
                Clear filters
              </button>
            </div>
          )}
        </>
      )}
      </>
      )}

      <AddEmployeeModal
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onEmployeeAdded={handleEmployeeAdded}
      />

      <AddEmployeeModal
        open={!!editEmployee}
        onClose={() => setEditEmployee(null)}
        employee={editEmployee}
        onEmployeeUpdated={handleEmployeeUpdated}
        onEmployeeAdded={() => {}}
      />

      <DeactivateConfirmModal
        open={!!deactivateTarget}
        employeeName={deactivateTarget?.employee?.fullName}
        shiftCount={deactivateTarget?.shiftCount}
        lastShiftDate={deactivateTarget?.lastShiftDate}
        defaultRemove={defaultRemoveUpcoming}
        inFlight={deactivateInFlight}
        onConfirm={handleConfirmDeactivate}
        onCancel={closeDeactivateModal}
      />

      <WhatsappInviteModal
        open={!!inviteEmployee}
        employee={inviteEmployee}
        submitting={inviteSubmitting}
        error={inviteError}
        consentChecked={inviteConsentChecked}
        ageChecked={inviteAgeChecked}
        onConsentChange={setInviteConsentChecked}
        onAgeChange={setInviteAgeChecked}
        onConfirm={handleConfirmInvite}
        onClose={closeInviteModal}
        gated={inviteGated}
        gatedMessage={inviteGatedMessage}
      />
    </div>
  );
}

export default Team;
